/**
 * Contact — shared PublicShell + homepage court-exhibit grammar (rules not fills,
 * DM Serif headings, DM Mono labels/meta, Outfit body, squared CTA). Submission
 * logic (api.post /api/v1/contact) + the success state are unchanged.
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PublicShell from '../components/marketing/PublicShell';
import { api } from '../utils/api';
import { AuthField, AuthSelect, AuthTextarea, AuthSubmit, AuthError } from '../components/auth/authForm';

const DETAILS: Array<{ label: string; lines: React.ReactNode }> = [
  { label: 'Email', lines: <>support@claimsync.io<br />sales@claimsync.io</> },
  { label: 'Phone', lines: <>+1 (251) 605-2934<br /><span className="text-text-dim">Mon–Fri · 9am–5pm EST</span></> },
  { label: 'Address', lines: <>The Figure Co., Inc.<br />1961 Karla Ct<br />Auburn, AL 36830</> },
  { label: 'Response time', lines: <>Within 24 business hours. Flag anything urgent in your message.</> },
];

export default function ContactPage() {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    try {
      await api.post('/api/v1/contact', { ...formData });
      setStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch {
      setStatus('error');
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  return (
    <PublicShell wide>
      <section className="mx-auto max-w-3xl px-5 pb-10 pt-6 text-center sm:px-8">
        <p className="font-mono text-xs uppercase tracking-[0.22em] text-text-dim">Contact</p>
        <h1 className="mt-5 font-heading text-4xl leading-tight tracking-tight text-text-primary md:text-5xl">
          Get in touch
        </h1>
        <p className="mt-5 font-body text-lg leading-relaxed text-text-secondary">
          Questions about the platform, a private-beta seat, or a specific claim? We’d like to hear from you.
        </p>
      </section>

      <div className="mx-auto max-w-[1100px] px-5 py-12 sm:px-8 lg:py-16">
        <div className="grid gap-12 md:grid-cols-[0.8fr_1.2fr] md:gap-16">
          {/* Contact details — ruled register, no icon chips */}
          <div>
            <h2 className="font-heading text-2xl tracking-tight text-text-primary">Reach us</h2>
            <dl className="mt-6 border-t border-hairline">
              {DETAILS.map((d) => (
                <div key={d.label} className="border-b border-hairline py-4">
                  <dt className="font-mono text-[11px] uppercase tracking-[0.12em] text-text-dim">{d.label}</dt>
                  <dd className="mt-1.5 font-body text-sm leading-relaxed text-text-secondary">{d.lines}</dd>
                </div>
              ))}
            </dl>
          </div>

          {/* Form */}
          <div>
            <h2 className="font-heading text-2xl tracking-tight text-text-primary">Send a message</h2>
            {status === 'success' ? (
              <div className="mt-6 border-l-2 border-hairline pl-6">
                <p className="font-heading text-lg text-text-primary">Message sent.</p>
                <p className="mt-2 font-body text-sm leading-relaxed text-text-secondary">
                  Thanks for reaching out — we’ll get back to you shortly.
                </p>
                <button
                  type="button"
                  onClick={() => setStatus('idle')}
                  className="mt-4 inline-flex font-mono text-[11px] uppercase tracking-[0.12em] text-text-primary underline decoration-[var(--color-border-strong)] underline-offset-4 transition-colors hover:text-brand-text hover:decoration-current"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <AuthField id="name" name="name" label="Full name" value={formData.name} onChange={handleChange} required placeholder="John Doe" autoComplete="name" />
                  <AuthField id="email" name="email" label="Email address" type="email" value={formData.email} onChange={handleChange} required placeholder="john@firm.com" autoComplete="email" />
                </div>
                <AuthSelect id="subject" name="subject" label="Subject" value={formData.subject} onChange={handleChange} required>
                  <option value="">Select a subject…</option>
                  <option value="general">General inquiry</option>
                  <option value="support">Technical support</option>
                  <option value="sales">Sales question</option>
                  <option value="partnership">Partnership opportunity</option>
                  <option value="feedback">Feedback</option>
                  <option value="other">Other</option>
                </AuthSelect>
                <AuthTextarea id="message" name="message" label="Message" value={formData.message} onChange={handleChange} required placeholder="How can we help?" rows={6} />

                {status === 'error' && <AuthError>Something went wrong. Please try again.</AuthError>}

                <AuthSubmit loading={status === 'loading'} loadingText="Sending…">
                  Send message
                </AuthSubmit>

                <p className="font-mono text-[11px] leading-relaxed text-text-dim">
                  By submitting this form you agree to our{' '}
                  <Link to="/privacy" className="underline decoration-[var(--color-border-strong)] underline-offset-2 hover:text-text-secondary hover:decoration-current">
                    Privacy Policy
                  </Link>.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </PublicShell>
  );
}
